import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { useBasePath } from '@/lib/paths'
import { Shield, Plus } from 'lucide-react'

type RegisteredAgent = {
  id: string
  stage: string
  type_name: string
  jurisdiction_label: string
  business_name: string
  duration_months: number
  created_at: string
}

type Props = {
  registered_agents: RegisteredAgent[]
  search: string
  pagination: PaginationMeta
}

export default function RegisteredAgentsIndex({ registered_agents, search, pagination }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout title="Registered Agent" breadcrumbs={[{ label: 'Registered Agent' }]}>
      <div>
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Registered Agent</h1>
          <p className="text-sm text-muted-foreground">Manage your registered agent services</p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm">
              <Link href={`${basePath}/registered_agents/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                New Service
              </Link>
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <SearchInput value={search} placeholder="Search registered agents..." />
        </div>

        {registered_agents.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <Shield className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No registered agent services yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Get started by applying for registered agent service</p>
            <Button asChild size="sm">
              <Link href={`${basePath}/registered_agents/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Get Started
              </Link>
            </Button>
          </div>
        ) : (
          <div className="border rounded-lg">
            {registered_agents.map((ra, i) => (
              <Link
                key={ra.id}
                href={`${basePath}/registered_agents/${ra.id}`}
                className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Shield className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{ra.business_name}</span>
                      <StageBadge stage={ra.stage} variant="service" />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {ra.type_name} · {ra.jurisdiction_label} · {ra.duration_months} months
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <Pagination pagination={pagination} />
      </div>
    </DashboardLayout>
  )
}
